import {
  Button,
  Container,
  Flex,
  Image,
  Text,
  useToast,
  Heading,
  Spinner,
  Link,
} from "@chakra-ui/react"
import {
  Link as RouterLink,
  createFileRoute,
} from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { z } from "zod"

import Logo from "/assets/images/denborgin-logo.svg"
import { PublicService, type SaasVerifyResponse } from "../client"

const verifySaasSearchSchema = z.object({
  token: z.string().optional(),
})

export const Route = createFileRoute("/verify-saas")({
  component: VerifySaas,
  validateSearch: (search) => verifySaasSearchSchema.parse(search),
})

function VerifySaas() {
  const { token } = Route.useSearch()
  const toast = useToast()
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading")
  const [data, setData] = useState<SaasVerifyResponse | null>(null)
  const [errorMsg, setErrorMsg] = useState<string>("")

  useEffect(() => {
    if (!token) {
      setStatus("error")
      setErrorMsg("Brak tokena weryfikacyjnego.")
      return
    }

    const verify = async () => {
      try {
        const response = await PublicService.saasVerify({ token })
        setData(response)
        setStatus("success")
        toast({
          title: "Konto aktywowane",
          description: "Profil Twojej firmy jest gotowy.",
          status: "success",
          duration: 5000,
          isClosable: true,
        })
      } catch (err: any) {
        setStatus("error")
        setErrorMsg(err.body?.error || "Nie udało się zweryfikować konta. Link mógł wygasnąć.")
      }
    }

    verify()
  }, [token, toast])

  return (
    <Flex flexDir="column" justify="center" align="center" h="100vh" p={4}>
      <Container maxW="md" textAlign="center">
        <Image src={Logo} alt="Denborgin logo" maxW="2xs" mx="auto" mb={8} />

        {status === "loading" && (
          <Flex flexDir="column" align="center">
            <Spinner size="xl" color="blue.500" mb={4} />
            <Heading size="md" mb={2}>Weryfikacja konta...</Heading>
            <Text>Proszę czekać, konfigurujemy profil Twojej firmy.</Text>
          </Flex>
        )}

        {status === "success" && data && (
          <Flex flexDir="column" align="center">
            <Heading size="lg" mb={4} color="green.500">Konto aktywowane!</Heading>
            <Text fontSize="lg" mb={8}>
              Sukces! Twoja firma <strong>{data.subdomain}</strong> została zarejestrowana i jest aktywna.
            </Text>
            <Button as="a" href={data.login_url} variant="primary" size="lg" width="100%">
              Przejdź do logowania
            </Button>
          </Flex>
        )}

        {status === "error" && (
          <Flex flexDir="column" align="center">
            <Heading size="lg" mb={4} color="red.500">Weryfikacja nieudana</Heading>
            <Text fontSize="lg" mb={8} color="gray.600">
              {errorMsg}
            </Text>
            <Button as={RouterLink} to="/register-company" variant="primary" mb={4} width="100%">
              Spróbuj zarejestrować się ponownie
            </Button>
            <Link as={RouterLink} to="/login" color="blue.500">
              Powrót do logowania
            </Link>
          </Flex>
        )}
      </Container>
    </Flex>
  )
}
